import pkg from '../../../package.json';
import Datadog from '../../utils/trackers/Datadog';

const DEFAULT_SERVICE = 'claro-video-web';
const DEFAULT_SITE = 'datadoghq.com';
const DEFAULT_PRIVACY_LEVEL = 'mask-user-input';
const DEFAULT_RUM_CDN_VERSION = 'v7';
const DEFAULT_LOGS_CDN_VERSION = 'v6';
const DEFAULT_INIT_TIMEOUT_MS = 4000;
const DEFAULT_FORWARD_CONSOLE_LOGS = [];
const DEFAULT_FORWARD_REPORTS = [];
const diagnosticConsole = typeof console !== 'undefined'
  ? { info: console.info, warn: console.warn }
  : {};

function reportInitialization(status, details = {}) {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const diagnostic = { status, ...details };

  if (typeof window !== 'undefined') {
    window.__DATADOG_STATUS__ = diagnostic;
  }

  const log = status === 'failed' || status === 'missing_configuration'
    ? diagnosticConsole.warn
    : diagnosticConsole.info;

  if (typeof log === 'function') {
    log.call(console, '[Datadog]', diagnostic);
  }
}

function normalizeTracingUrl(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue || !/^https?:\/\//i.test(normalizedValue)) {
    return null;
  }

  return normalizedValue;
}

function getAllowedTracingUrls() {
  const browserOrigin = typeof window !== 'undefined' && window.location && window.location.origin
    ? `${window.location.origin}/webapi-video/`
    : '';

  const candidates = [
    'http://aaf-cvideo-tv-stale.clarovideo.net/webapi-video/',
    'http://aaf-cvideo-stb.clarovideo.net/webapi-video/',
    browserOrigin,
    'http://mw-cglobal-cvideo-stb.clarovideo.net',
    'http://mw-cglobal-cvideo-web.clarovideo.net',
    'http://www.claromusica.com',
  ]
    .map(normalizeTracingUrl)
    .filter(Boolean);

  return Array.from(new Set(candidates));
}

function getEnvValue(...keys) {
  for (let index = 0; index < keys.length; index += 1) {
    const value = process.env[keys[index]];

    if (typeof value !== 'undefined' && value !== null && value !== '') {
      return value;
    }
  }

  return undefined;
}

function parseBoolean(value, fallback) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === '1') {
    return true;
  }

  if (normalizedValue === 'false' || normalizedValue === '0') {
    return false;
  }

  return fallback;
}

function parseNumber(value, fallback) {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function getRumConfiguration() {
  const applicationId = getEnvValue('REACT_APP_DATADOG_APPLICATION_ID');
  const clientToken = getEnvValue('REACT_APP_DATADOG_CLIENT_TOKEN');
  const site = getEnvValue('REACT_APP_DATADOG_SITE');
  const service = getEnvValue('REACT_APP_DATADOG_SERVICE');
  const env = getEnvValue('REACT_APP_DATADOG_ENV');
  const sessionSampleRate = getEnvValue('REACT_APP_DATADOG_SESSION_SAMPLE_RATE');
  const sessionReplaySampleRate = getEnvValue('REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE');
  const startSessionReplayRecordingManually = getEnvValue(
    'REACT_APP_DATADOG_START_SESSION_REPLAY_RECORDING_MANUALLY',
    'DATADOG_START_SESSION_REPLAY_RECORDING_MANUALLY'
  );
  const forceSessionReplayRecording = getEnvValue(
    'REACT_APP_DATADOG_FORCE_SESSION_REPLAY_RECORDING',
    'DATADOG_FORCE_SESSION_REPLAY_RECORDING'
  );
  const trackUserInteractions = getEnvValue('REACT_APP_DATADOG_TRACK_USER_INTERACTIONS');
  const trackResources = getEnvValue('REACT_APP_DATADOG_TRACK_RESOURCES');
  const trackLongTasks = getEnvValue('REACT_APP_DATADOG_TRACK_LONG_TASKS');
  const defaultPrivacyLevel = getEnvValue('REACT_APP_DATADOG_DEFAULT_PRIVACY_LEVEL');
  const nodeEnv = getEnvValue('NODE_ENV');
  const allowedTracingUrls = getAllowedTracingUrls();

  if (!applicationId || !clientToken) {
    return null;
  }

  return {
    applicationId,
    clientToken,
    site: site || DEFAULT_SITE,
    service: service || DEFAULT_SERVICE,
    env: env || nodeEnv || 'development',
    version: pkg.version,
    sessionSampleRate: parseNumber(sessionSampleRate, 100),
    sessionReplaySampleRate: parseNumber(sessionReplaySampleRate, 0),
    startSessionReplayRecordingManually: parseBoolean(startSessionReplayRecordingManually, false),
    forceSessionReplayRecording: parseBoolean(forceSessionReplayRecording, false),
    trackUserInteractions: parseBoolean(trackUserInteractions, true),
    trackResources: parseBoolean(trackResources, true),
    trackLongTasks: parseBoolean(trackLongTasks, true),
    defaultPrivacyLevel: defaultPrivacyLevel || DEFAULT_PRIVACY_LEVEL,
    allowedTracingUrls,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: DEFAULT_FORWARD_CONSOLE_LOGS,
    forwardReports: DEFAULT_FORWARD_REPORTS,
  };
}

function getLogsConfiguration(rumConfiguration) {
  const logsEnabled = parseBoolean(getEnvValue('REACT_APP_DATADOG_LOGS_ENABLED'), false);
  const forwardErrorsToLogs = parseBoolean(
    getEnvValue('REACT_APP_DATADOG_LOGS_FORWARD_ERRORS'),
    true
  );

  if (!logsEnabled || !rumConfiguration) {
    return null;
  }

  return {
    enabled: true,
    clientToken: getEnvValue('REACT_APP_DATADOG_LOGS_CLIENT_TOKEN') || rumConfiguration.clientToken,
    site: rumConfiguration.site,
    service: rumConfiguration.service,
    env: rumConfiguration.env,
    version: rumConfiguration.version,
    sessionSampleRate: parseNumber(getEnvValue('REACT_APP_DATADOG_LOGS_SESSION_SAMPLE_RATE'), rumConfiguration.sessionSampleRate),
    forwardErrorsToLogs,
    forwardConsoleLogs: DEFAULT_FORWARD_CONSOLE_LOGS,
    forwardReports: DEFAULT_FORWARD_REPORTS,
  };
}

function getCdnConfiguration(rumConfiguration, logsConfiguration) {
  return {
    rumVersion: getEnvValue('REACT_APP_DATADOG_RUM_CDN_VERSION') || DEFAULT_RUM_CDN_VERSION,
    logsVersion: getEnvValue('REACT_APP_DATADOG_LOGS_CDN_VERSION') || DEFAULT_LOGS_CDN_VERSION,
    rumCdnUrl: getEnvValue('REACT_APP_DATADOG_RUM_CDN_URL') || '',
    logsCdnUrl: logsConfiguration ? getEnvValue('REACT_APP_DATADOG_LOGS_CDN_URL') || '' : '',
    site: rumConfiguration.site,
    timeoutMs: parseNumber(getEnvValue('REACT_APP_DATADOG_INIT_TIMEOUT_MS'), DEFAULT_INIT_TIMEOUT_MS),
  };
}

export default async function initDatadog(context = {}) {
  const integrationEnabled = parseBoolean(getEnvValue('REACT_APP_DATADOG_ENABLED'), true);

  if (!integrationEnabled) {
    Datadog.disable();
    reportInitialization('disabled');
    return false;
  }

  Datadog.enable();

  const configuration = getRumConfiguration();

  if (!configuration) {
    Datadog.disable();
    reportInitialization('missing_configuration', {
      missingVariables: [
        'REACT_APP_DATADOG_APPLICATION_ID',
        'REACT_APP_DATADOG_CLIENT_TOKEN',
      ].filter(key => !getEnvValue(key)),
    });
    return false;
  }

  const logsConfiguration = getLogsConfiguration(configuration);
  const diagnostic = {
    site: configuration.site,
    service: configuration.service,
    env: configuration.env,
    sessionSampleRate: configuration.sessionSampleRate,
    logsEnabled: Boolean(logsConfiguration),
  };

  reportInitialization('initializing', diagnostic);

  const initialized = await Datadog.initialize({
    rum: configuration,
    logs: logsConfiguration,
    cdn: getCdnConfiguration(configuration, logsConfiguration),
  });

  if (!initialized) {
    Datadog.disable();
    reportInitialization('failed', diagnostic);
    return false;
  }

  Datadog.setGlobalContext({
    appName: pkg.name,
    appVersion: pkg.version,
    device: context.device || '',
    subplatform: context.subplatform || '',
  });

  reportInitialization('initialized', diagnostic);

  return true;
}



// WEBPACK FOOTER //
// ./src/services/datadog/init.js